import { 
  PlatformAdapter, 
  PermissionStatus, 
  NotificationOptions, 
  NotificationChannel,
  PendingNotification,
  DeliveredNotification,
  ReceivedNotification
} from '../types';

/**
 * Web通知适配器
 */
export class WebNotificationAdapter implements PlatformAdapter {
  private serviceWorkerRegistration?: ServiceWorkerRegistration;
  private pendingNotifications: Map<string, { options: NotificationOptions, timer?: number }> = new Map();
  private deliveredNotifications: Map<string, { notification: Notification, deliveredAt: Date }> = new Map();
  
  constructor() {
    this.initServiceWorker();
  }
  
  /**
   * 初始化Service Worker
   */
  private async initServiceWorker() {
    if ('serviceWorker' in navigator) {
      try {
        // 尝试注册或获取已存在的Service Worker
        this.serviceWorkerRegistration = await navigator.serviceWorker.ready;
        console.log('[WebAdapter] Service worker ready');

        // 如果没有找到已注册的Service Worker，尝试注册
        if (!this.serviceWorkerRegistration) {
          console.log('[WebAdapter] Registering new service worker');
          this.serviceWorkerRegistration = await navigator.serviceWorker.register('/notification-sw.js', {
            scope: '/'
          });
        }

        // 监听 push 事件
        navigator.serviceWorker.addEventListener('message', (event) => {
          if (event.data && event.data.type === 'PUSH_NOTIFICATION') {
            console.log('[WebAdapter] Received push notification from service worker:', event.data.notification);
            this.notificationReceiveHandler(event.data.notification);
          }
        });
      } catch (error) {
        console.error('[WebAdapter] Service worker initialization error:', error);
      }
    } else {
      console.warn('[WebAdapter] Service workers not supported in this browser');
    }
  }
  
  /**
   * 请求通知权限
   */
  async requestPermission(): Promise<PermissionStatus> {
    if (!('Notification' in window)) {
      console.warn('[WebAdapter] Notifications not supported');
      return 'denied';
    }
    
    try {
      const permission = await Notification.requestPermission();
      return permission as PermissionStatus;
    } catch (error) {
      console.error('[WebAdapter] Permission request error:', error);
      return 'denied';
    }
  }
  
  /**
   * 检查通知权限
   */
  async checkPermission(): Promise<PermissionStatus> {
    if (!('Notification' in window)) {
      return 'denied';
    }
    
    return Notification.permission as PermissionStatus;
  }
  
  /**
   * 发送本地通知
   */
  async scheduleLocalNotification(options: NotificationOptions): Promise<string> {
    // 检查权限
    if (!('Notification' in window)) {
      throw new Error('Notifications not supported in this browser');
    }
    
    if (Notification.permission !== 'granted') {
      throw new Error('Notification permission not granted');
    }
    
    try {
      // 确保ID
      const id = options.id || Math.random().toString(36).substring(2, 10);
      
      // 如果有调度，设置延迟通知
      if (options.schedule?.at) {
        const delay = options.schedule.at.getTime() - Date.now();
        if (delay > 0) {
          // 存储待处理通知
          this.pendingNotifications.set(id, { options });
          
          // 设置定时器
          const timer = window.setTimeout(() => {
            this.showWebNotification(id, options);
            this.pendingNotifications.delete(id);
          }, delay);
          
          // 更新待处理通知中的定时器ID
          this.pendingNotifications.set(id, { options, timer });
          
          return id;
        }
      }
      
      // 即时显示通知
      return this.showWebNotification(id, options);
    } catch (error) {
      console.error('[WebAdapter] Schedule notification error:', error);
      throw error;
    }
  }
  
  /**
   * 显示Web通知
   */
  private showWebNotification(id: string, options: NotificationOptions): string {
    try {
      let notification: Notification;
      
      // 尝试使用Service Worker显示通知（优先）
      if (this.serviceWorkerRegistration) {
        const notificationOptions: NotificationOptions & { badge?: string, tag?: string } = {
          ...options,
          id
        };
        
        // 为Service Worker通知添加额外选项
        const swOptions = {
          body: options.body,
          icon: options.icon || '/icon.png',
          badge: '/badge-icon.png', // 通知徽章图标
          tag: options.groupId || id, // 用于分组通知
          data: { ...options.extra, id, source: 'local' as const }
        };
        
        // 尝试使用Service Worker显示通知
        this.serviceWorkerRegistration.showNotification(options.title, swOptions)
          .then(() => {
            console.log('[WebAdapter] Notification shown via Service Worker:', id);
            
            // 记录已递送通知（虽然我们无法直接获取Notification对象）
            this.deliveredNotifications.set(id, {
              // 创建一个模拟的Notification对象
              notification: {
                title: options.title,
                body: options.body || '',
                data: options.extra,
                close: () => {
                  // 尝试通过Service Worker关闭通知
                  if (this.serviceWorkerRegistration) {
                    this.serviceWorkerRegistration.getNotifications({ tag: options.groupId || id })
                      .then(notifications => {
                        notifications.forEach(n => n.close());
                      })
                      .catch(err => console.error('[WebAdapter] Error closing SW notification:', err));
                  }
                }
              } as unknown as Notification,
              deliveredAt: new Date()
            });
          })
          .catch(error => {
            console.error('[WebAdapter] Error showing notification via Service Worker:', error);
            // 如果Service Worker显示失败，回退到普通通知
            this.showFallbackNotification(id, options);
          });
          
        return id;
      } else {
        // 使用普通Web通知作为回退选项
        return this.showFallbackNotification(id, options);
      }
    } catch (error) {
      console.error('[WebAdapter] Show notification error:', error);
      // 如果出错，尝试使用回退选项
      return this.showFallbackNotification(id, options);
    }
  }
  
  /**
   * 显示普通Web通知（回退选项）
   */
  private showFallbackNotification(id: string, options: NotificationOptions): string {
    try {
      // 创建普通Web通知
      const notification = new Notification(options.title, {
        body: options.body,
        icon: options.icon || '/icon.png',
        data: { ...options.extra, id, source: 'local' }
      });
      
      // 记录已递送通知
      this.deliveredNotifications.set(id, {
        notification,
        deliveredAt: new Date()
      });
      
      // 处理点击事件
      notification.onclick = () => {
        window.focus();
        notification.close();
        
        // 触发操作回调
        this.notificationActionHandler({
          id,
          title: options.title,
          body: options.body,
          data: options.extra,
          source: 'local'
        });
      };
      
      // 处理关闭事件
      notification.onclose = () => {
        // 如果已经不在已递送通知列表，说明是通过操作关闭的，不再处理
        if (!this.deliveredNotifications.has(id)) return;
        
        // 移除已递送通知记录
        this.deliveredNotifications.delete(id);
      };
      
      return id;
    } catch (error) {
      console.error('[WebAdapter] Show fallback notification error:', error);
      throw error;
    }
  }
  
  /**
   * 通知接收处理
   */
  private notificationReceiveHandler(notification: ReceivedNotification) {
    try {
      // 获取NotificationService实例并触发回调
      // 延迟导入NotificationService以避免循环依赖
      import('../NotificationService').then(({ NotificationService }) => {
        const service = NotificationService.getInstance();
        service.triggerReceivedSubscriptions(notification);
      });
    } catch (error) {
      console.error('[WebAdapter] Notification receive handler error:', error);
    }
  }
  
  /**
   * 通知操作处理
   */
  private notificationActionHandler(notification: ReceivedNotification) {
    try {
      // 获取NotificationService实例并触发回调
      // 延迟导入NotificationService以避免循环依赖
      import('../NotificationService').then(({ NotificationService }) => {
        const service = NotificationService.getInstance();
        service.triggerActionSubscriptions(notification);
      });
    } catch (error) {
      console.error('[WebAdapter] Notification action handler error:', error);
    }
  }
  
  /**
   * 取消本地通知
   */
  async cancelLocalNotification(id: string): Promise<void> {
    try {
      // 检查并取消待处理通知
      if (this.pendingNotifications.has(id)) {
        const pending = this.pendingNotifications.get(id);
        if (pending?.timer) {
          clearTimeout(pending.timer);
        }
        this.pendingNotifications.delete(id);
      }
      
      // 检查并关闭已递送通知
      if (this.deliveredNotifications.has(id)) {
        const delivered = this.deliveredNotifications.get(id);
        if (delivered?.notification) {
          delivered.notification.close();
        }
        this.deliveredNotifications.delete(id);
      }
      
      // 如果有Service Worker，也尝试关闭Service Worker显示的通知
      if (this.serviceWorkerRegistration) {
        try {
          const notifications = await this.serviceWorkerRegistration.getNotifications({ tag: id });
          notifications.forEach(notification => notification.close());
        } catch (error) {
          console.error('[WebAdapter] Error closing service worker notification:', error);
        }
      }
    } catch (error) {
      console.error('[WebAdapter] Cancel notification error:', error);
      throw error;
    }
  }
  
  /**
   * 取消所有本地通知
   */
  async cancelAllLocalNotifications(): Promise<void> {
    try {
      // 取消所有待处理通知
      for (const [id, pending] of this.pendingNotifications.entries()) {
        if (pending.timer) {
          clearTimeout(pending.timer);
        }
      }
      this.pendingNotifications.clear();
      
      // 关闭所有已递送通知
      for (const [id, delivered] of this.deliveredNotifications.entries()) {
        if (delivered.notification) {
          delivered.notification.close();
        }
      }
      this.deliveredNotifications.clear();
      
      // 如果有Service Worker，也尝试关闭所有Service Worker显示的通知
      if (this.serviceWorkerRegistration) {
        try {
          const notifications = await this.serviceWorkerRegistration.getNotifications();
          notifications.forEach(notification => notification.close());
        } catch (error) {
          console.error('[WebAdapter] Error closing all service worker notifications:', error);
        }
      }
    } catch (error) {
      console.error('[WebAdapter] Cancel all notifications error:', error);
      throw error;
    }
  }
  
  /**
   * 获取待处理通知
   */
  async getPendingNotifications(): Promise<PendingNotification[]> {
    try {
      return Array.from(this.pendingNotifications.entries()).map(([id, { options }]) => ({
        id,
        title: options.title,
        body: options.body,
        schedule: options.schedule ? {
          at: options.schedule.at
        } : undefined,
        extra: options.extra
      }));
    } catch (error) {
      console.error('[WebAdapter] Get pending notifications error:', error);
      return [];
    }
  }
  
  /**
   * 获取已递送通知
   */
  async getDeliveredNotifications(): Promise<DeliveredNotification[]> {
    try {
      // 合并本地记录和Service Worker通知
      const deliveredFromLocal = Array.from(this.deliveredNotifications.entries()).map(([id, { notification, deliveredAt }]) => ({
        id,
        title: notification.title,
        body: notification.body || '',
        extra: notification.data,
        deliveredAt
      }));
      
      // 如果有Service Worker，尝试获取Service Worker显示的通知
      if (this.serviceWorkerRegistration) {
        try {
          const swNotifications = await this.serviceWorkerRegistration.getNotifications();
          const deliveredFromSW = swNotifications.map(notification => ({
            id: (notification.data as any)?.id || Math.random().toString(36).substring(2, 10),
            title: notification.title,
            body: notification.body || '',
            extra: notification.data,
            deliveredAt: new Date() // 无法获取确切的递送时间
          }));
          
          // 合并并去除重复项
          const allDelivered = [...deliveredFromLocal];
          deliveredFromSW.forEach(swNote => {
            if (!allDelivered.some(localNote => localNote.id === swNote.id)) {
              allDelivered.push(swNote);
            }
          });
          
          return allDelivered;
        } catch (error) {
          console.error('[WebAdapter] Error getting service worker notifications:', error);
        }
      }
      
      return deliveredFromLocal;
    } catch (error) {
      console.error('[WebAdapter] Get delivered notifications error:', error);
      return [];
    }
  }
  
  /**
   * 移除已递送通知
   */
  async removeDeliveredNotifications(ids: string[]): Promise<void> {
    try {
      for (const id of ids) {
        // 移除本地记录的通知
        if (this.deliveredNotifications.has(id)) {
          const delivered = this.deliveredNotifications.get(id);
          if (delivered?.notification) {
            delivered.notification.close();
          }
          this.deliveredNotifications.delete(id);
        }
        
        // 如果有Service Worker，尝试关闭Service Worker显示的通知
        if (this.serviceWorkerRegistration) {
          try {
            const notifications = await this.serviceWorkerRegistration.getNotifications({ tag: id });
            notifications.forEach(notification => notification.close());
          } catch (error) {
            console.error(`[WebAdapter] Error removing service worker notification ${id}:`, error);
          }
        }
      }
    } catch (error) {
      console.error('[WebAdapter] Remove delivered notifications error:', error);
      throw error;
    }
  }
  
  /**
   * 创建通知频道（Web不支持，空实现）
   */
  async createChannel(channel: NotificationChannel): Promise<void> {
    // Web平台不支持通知频道
    console.log('[WebAdapter] Channels not supported in Web, ignoring:', channel.id);
    return;
  }
  
  /**
   * 注册推送通知（需要Service Worker支持）
   */
  async registerForPushNotifications(): Promise<string> {
    if (!('PushManager' in window) || !this.serviceWorkerRegistration) {
      throw new Error('Push notifications not supported in this browser or service worker not registered');
    }
    
    try {
      const permission = await this.requestPermission();
      if (permission !== 'granted') {
        throw new Error('Notification permission denied');
      }
      
      // 订阅推送服务
      const subscriptionOptions = {
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(
          // 这里应该放入您的VAPID公钥
          process.env.WEB_PUSH_PUBLIC_KEY || ''
        )
      };
      
      const pushSubscription = await this.serviceWorkerRegistration.pushManager.subscribe(subscriptionOptions);
      
      // 将订阅信息转换为字符串（实际应用中应发送到服务器）
      const subscription = JSON.stringify(pushSubscription);
      console.log('[WebAdapter] Push subscription:', subscription);
      
      return subscription;
    } catch (error) {
      console.error('[WebAdapter] Error registering for push notifications:', error);
      throw error;
    }
  }
  
  /**
   * 注销推送通知
   */
  async unregisterForPushNotifications(): Promise<void> {
    if (!this.serviceWorkerRegistration) {
      console.warn('[WebAdapter] Service worker not registered, cannot unregister push');
      return;
    }
    
    try {
      const subscription = await this.serviceWorkerRegistration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
        console.log('[WebAdapter] Successfully unsubscribed from push notifications');
      }
    } catch (error) {
      console.error('[WebAdapter] Error unregistering push notifications:', error);
      throw error;
    }
  }
  
  /**
   * 将Base64编码的应用服务器密钥转换为Uint8Array
   */
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    if (!base64String) {
      console.warn('[WebAdapter] No application server key provided');
      return new Uint8Array();
    }
    
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
      
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    
    return outputArray;
  }
} 